"use client";

import type { useChat } from "@ai-sdk/react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { logger } from "@/lib/logger";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { CreateDashboardItemInput, DashboardItemType } from "./schema";

const NodeNameQuery = graphql(`
  query CreateDashboardItemNodeName($featureId: ID!) {
    feature: node(id: $featureId) {
      __typename
      ... on SearchAnalyticsProject { name }
      ... on UserAnalyticsProject { name }
      ... on IndexWorker { name }
      ... on SearchAnalyticsView { name }
      ... on UserAnalyticsView { name }
    }
  }
`);

const TYPE_LABELS: Record<DashboardItemType, ReturnType<typeof msg>> = {
  SA_CLICK: msg`Clicks`,
  SA_IMP: msg`Impressions`,
  SA_CTR: msg`CTR`,
  SA_RANK: msg`Avg. position`,
  SA_UNIQ_QUERY: msg`Unique queries`,
  SA_UNIQ_URL: msg`Unique URLs`,
  IW_CRAWL: msg`Crawled rate`,
  IW_INDEX: msg`Indexed rate`,
  IW_NOT_INDEX: msg`Not indexed rate`,
  UA_SESSION_COUNT: msg`Sessions`,
  UA_UNIQUE_USER_COUNT: msg`Unique users`,
  UA_CONVERSION_RATE: msg`Conversion rate`,
  UA_SURF_RATE: msg`Surf rate`,
  UA_ENGAGEMENT_RATE: msg`Engagement rate`,
  UA_CONVERSION_COUNT: msg`Sessions with conversions`,
  UA_PAGE_VIEW_EVENT_COUNT: msg`Page views`,
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateDashboardItemConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();

  const input = (part.input ?? {}) as Partial<CreateDashboardItemInput>;

  const [{ data, fetching }] = useQuery({
    query: NodeNameQuery,
    variables: {
      featureId: input.featureId
        ? convertSaOrUaIdToProjectId(input.featureId)
        : "",
    },
    pause: !input.featureId,
  });

  const isTarget =
    isToolUIPart(part) && getToolName(part) === "create_dashboard_item";
  if (!isTarget || part.state !== "input-available") return null;

  const featureName = (() => {
    if (!input.featureId) return undefined;
    if (fetching)
      return <Skeleton className="h-4 w-28 bg-gray-400 dark:bg-gray-600" />;
    const node = data?.feature;
    switch (node?.__typename) {
      case "IndexWorker":
      case "SearchAnalyticsView":
      case "UserAnalyticsView":
      case "SearchAnalyticsProject":
      case "UserAnalyticsProject":
        return node.name ?? "-";
      default:
        return "-";
    }
  })();

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to create this dashboard item?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Type: </Trans>
          <span className="font-medium">
            {input.type ? i18n._(TYPE_LABELS[input.type]) : "-"}
          </span>
        </li>
        {featureName && (
          <li>
            <Trans>Source: </Trans>
            <span className="font-medium">{featureName}</span>
          </li>
        )}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Create</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}

function convertSaOrUaIdToProjectId(id: string): string {
  try {
    const decoded = Buffer.from(id, "base64").toString("utf8");
    const [kind, path] = decoded.split(":");
    if (!path) return id;
    if (kind !== "SearchAnalytics" && kind !== "UserAnalytics") {
      return id;
    }
    if (!path) return id;
    const paths = path.split("/");
    // format: Project:customers/{customerId}/projects/{projectId}
    const newKind = "Project";
    const newPath = paths.slice(0, 4).join("/");
    const newId = `${newKind}:${newPath}`;
    return Buffer.from(newId).toString("base64");
  } catch (e) {
    logger.warn(e);
    return id; // echo back the original ID if error occurs
  }
}
